import React, { useState, useEffect, useCallback} from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { AlertCircle, CheckCircle, Lock, Search } from 'lucide-react';
import { toast } from 'react-hot-toast';

const BillingBlocksPage = () => {
  const [blocks, setBlocks] = useState([]);
  const [filteredBlocks, setFilteredBlocks] = useState([]);
  const [blockTypes, setBlockTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newBlock, setNewBlock] = useState({
    document_type: 'SO',
    document_number: '',
    block_type: '01',
    block_reason: '',
    blocked_by: 'USER',
    auto_block: false
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const fetchBlocks = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/billing-blocks/blocks`);
      const data = await response.json();
      setBlocks(data);
    } catch (error) {
      toast.error('Failed to load billing blocks');
    }
  }, [backendUrl]);

  const fetchBlockTypes = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/billing-blocks/block-types`);
      const data = await response.json();
      setBlockTypes(data);
    } catch (error) {
      console.error('Failed to load block types');
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchBlocks();
    fetchBlockTypes();
  }, [fetchBlocks, fetchBlockTypes]);

  const filterBlocks = useCallback(() => {
    let filtered = blocks;

    if (searchTerm) {
      filtered = filtered.filter(block => 
        block.document_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        block.block_id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(block => block.status === statusFilter);
    }

    setFilteredBlocks(filtered);
  }, [blocks, searchTerm, statusFilter]);

  useEffect(() => {
    filterBlocks();
  }, [filterBlocks]);

  const handleCreateBlock = async () => {
    if (!newBlock.document_number || !newBlock.block_reason) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/billing-blocks/blocks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newBlock)
      });

      if (response.ok) {
        toast.success('Billing block created successfully');
        setShowCreateDialog(false);
        fetchBlocks();
        setNewBlock({
          document_type: 'SO',
          document_number: '',
          block_type: '01',
          block_reason: '',
          blocked_by: 'USER',
          auto_block: false
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create block');
      }
    } catch (error) {
      toast.error('Error creating billing block');
    } finally {
      setLoading(false);
    }
  };

  const handleReleaseBlock = async (blockId) => {
    if (!window.confirm('Are you sure you want to release this block?')) return;

    try {
      const response = await fetch(`${backendUrl}/api/billing-blocks/blocks/${blockId}/release`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          released_by: 'USER',
          release_reason: 'Manual release'
        })
      });

      if (response.ok) {
        toast.success('Block released successfully');
        fetchBlocks();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to release block');
      }
    } catch (error) {
      toast.error('Error releasing block');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Active': { color: 'bg-red-100 text-red-800', icon: Lock },
      'Released': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'Cancelled': { color: 'bg-gray-100 text-gray-800', icon: AlertCircle }
    };

    const config = statusConfig[status] || statusConfig['Active'];
    const Icon = config.icon;

    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </Badge>
    );
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Billing Blocks</h1>
            <p className="text-gray-600">Manage order release controls and billing blocks</p>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-orange-600 hover:bg-orange-700">
                <Lock className="w-4 h-4 mr-2" />
                Create Block
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create Billing Block</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Document Type</label>
                  <Select value={newBlock.document_type} onValueChange={(value) => setNewBlock({...newBlock, document_type: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SO">Sales Order</SelectItem>
                      <SelectItem value="DL">Delivery</SelectItem>
                      <SelectItem value="BL">Billing Document</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Document Number *</label>
                  <Input
                    value={newBlock.document_number}
                    onChange={(e) => setNewBlock({...newBlock, document_number: e.target.value})}
                    placeholder="Enter document number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Block Type</label>
                  <Select value={newBlock.block_type} onValueChange={(value) => setNewBlock({...newBlock, block_type: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {blockTypes.map(type => (
                        <SelectItem key={type.code} value={type.code}>
                          {type.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Block Reason *</label>
                  <Input
                    value={newBlock.block_reason}
                    onChange={(e) => setNewBlock({...newBlock, block_reason: e.target.value})}
                    placeholder="Enter block reason"
                  />
                </div>
                <div className="flex justify-end space-x-2 mt-6">
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                  <Button onClick={handleCreateBlock} disabled={loading}>
                    {loading ? 'Creating...' : 'Create Block'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by document or block ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Released">Released</SelectItem>
                <SelectItem value="Cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Blocks Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Block ID</TableHead>
                <TableHead>Document Type</TableHead>
                <TableHead>Document Number</TableHead>
                <TableHead>Block Type</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Blocked On</TableHead>
                <TableHead>Blocked By</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBlocks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                    No billing blocks found
                  </TableCell>
                </TableRow>
              ) : (
                filteredBlocks.map((block) => (
                  <TableRow key={block.block_id}>
                    <TableCell className="font-medium">{block.block_id}</TableCell>
                    <TableCell>{block.document_type}</TableCell>
                    <TableCell>{block.document_number}</TableCell>
                    <TableCell>{block.block_type}</TableCell>
                    <TableCell className="max-w-xs truncate">{block.block_reason}</TableCell>
                    <TableCell>{block.blocked_on}</TableCell>
                    <TableCell>{block.blocked_by}</TableCell>
                    <TableCell>{getStatusBadge(block.status)}</TableCell>
                    <TableCell>
                      {block.status === 'Active' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReleaseBlock(block.block_id)}
                        >
                          Release
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredBlocks.length} of {blocks.length} billing blocks
        </div>
      </div>
    </Layout>
  );
};

export default BillingBlocksPage;